import { DeleteConfirmWithButton } from '@components/DeleteConfirm';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import {
  ControlledTextFormField,
  CountryDropdownFormField,
  DropdownFormField,
  MaskedNumberFormField,
  RangeDateFormField,
  SimpleInputRight,
} from '@elements/FormField';
import { yupResolver } from '@hookform/resolvers/yup';
import { CommonUtility } from '@utility/common';
import { DateUtility } from '@utility/date';
import SCMService from '@utility/services/scm';
import { ToastMessage } from '@utility/toast';
import { SeasonalityEventSchema } from '@utility/validations';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

const EVENT_TYPE_OPTIONS = [
  { text: 'Seasonality', value: 'seasonality' },
  { text: 'Lightning Deal', value: 'lightning_deal' },
];

const todayDate = new Date();
const nextWeekDate = new Date(new Date().setDate(todayDate.getDate() + 7));

const defaultValues = {
  type: EVENT_TYPE_OPTIONS[0].value,
  date: [todayDate, nextWeekDate],
  name: '',
  percentage: 1,
};

/**
 * Retrieves default form values based on lightning deals, seasonal spikes, and an event ID.
 * @param {Object[]} lightningDeal - Array of lightning deal objects.
 * @param {Object[]} seasonalSpike - Array of seasonal spike objects.
 * @param {string} eventId - The ID of the event.
 * @param {string} regionId - The ID of the region.
 * @returns {Object} - Default form values.
 */
const getFormDefaultValues = (
  lightningDeal,
  seasonalSpike,
  eventId,
  regionId,
) => {
  if (!eventId) {
    return { ...defaultValues, country: regionId };
  }

  const selected1 = lightningDeal.find(d => d._id === eventId);
  const selected2 = !selected1
    ? seasonalSpike.find(d => d._id === eventId)
    : null;
  const selected = selected2 || selected1;

  if (CommonUtility.isValidObject(selected)) {
    const isDeal = !!selected1;

    return {
      country: regionId,
      type: selected1 ? 'lightning_deal' : 'seasonality',
      date: [selected.from.substring(0, 10), selected.to.substring(0, 10)],
      name: selected.name,
      percentage:
        selected[isDeal ? 'units' : 'percentage'] - (isDeal ? 0 : 100),
    };
  }

  return { ...defaultValues, country: regionId };
};

/**
 * SeasonalityEventForm component for managing seasonality event details.
 * @param {string} props.eventId - The ID of the event.
 * @param {Function} props.forceRefresh - Function to force refresh the component.
 * @param {string} props.itemId - The ID of the item.
 * @param {Object[]} props.lightningDeal - Array of lightning deal objects.
 * @param {Object[]} props.marketplaceDropdown - Array of marketplace dropdown options.
 * @param {Function} props.refreshTimeline - Function to refresh the timeline.
 * @param {Array} props.regionalItemsList - Regions list
 * @param {string} props.regionId - The ID of the region.
 * @param {Object[]} props.seasonalSpike - Array of seasonal spike objects.
 * @param {Function} props.setAddEventCount - Function to set the count of added events.
 * @param {Function} props.setEventId - Function to set the event ID.
 * @param {Function} props.setFormData - Function to set the form data.
 * @returns {JSX.Element} - React component for managing seasonality event details.
 */
export function SeasonalityEventForm(props) {
  const {
    eventId,
    itemId,
    lightningDeal,
    marketplaceDropdown,
    refreshTimeline,
    regionalItemsList,
    seasonalSpike,
    setAddEventCount,
    setEventId,
    setFormData,
    regionId: masterRegionId,
    forceRefreshSales,
  } = props;
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formDefaultValues = useMemo(
    () =>
      getFormDefaultValues(
        lightningDeal,
        seasonalSpike,
        eventId,
        masterRegionId,
      ),
    [lightningDeal, seasonalSpike, eventId, masterRegionId],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    // @ts-expect-error - TS2322: Type 'Resolver<AssertsShape<Assign<ObjectShape, { type: StringSchema<string | un...
    resolver: yupResolver(SeasonalityEventSchema),
    defaultValues: formDefaultValues,
    shouldUnregister: true,
  });

  const formData = watch();

  const { type: eventType } = formData;

  const isLightningDeals = eventType === 'lightning_deal';
  const unitLabel = !isLightningDeals ? 'Percentage' : 'Units';

  const { marketplaceRegionId } = useMemo(() => {
    const id = formData.country || formDefaultValues.country;

    const item = (regionalItemsList || []).find(
      x => x._id === id || x.marketplace_region_id === id,
    );

    return {
      marketplaceRegionId: item?.marketplace_region_id,
    };
  }, [formData?.country, formDefaultValues?.country]);

  const getDefaultPayload = d => ({
    _id: d._id,
    from: DateUtility.dateToServerDate(d.from),
    marketplace_region_id: d.marketplace_region_id,
    name: d.name,
    to: DateUtility.dateToServerDate(d.to),
  });

  const submit = async (data, isDelete = false) => {
    try {
      setLoading(true);
      if (isDelete) setDeleting(true);
      const payload = {
        seasonalSpike:
          seasonalSpike?.map(d => ({
            ...getDefaultPayload(d),
            percentage: d.percentage || 0,
          })) || [],
        lightningDeal:
          lightningDeal?.map(d => ({
            ...getDefaultPayload(d),
            units: d.units || 0,
          })) || [],
      };

      if (isDelete && eventId) {
        const isDeal = formDefaultValues?.type === 'lightning_deal';
        const key = isDeal ? 'lightningDeal' : 'seasonalSpike';

        payload[key] = payload[key].map(d =>
          d._id !== eventId
            ? d
            : { _id: d._id, marketplace_region_id: d.marketplace_region_id },
        );
      } else {
        const isDeal = data?.type === 'lightning_deal';
        const key = isDeal ? 'lightningDeal' : 'seasonalSpike';

        const item = {
          name: data.name,
          from: DateUtility.dateToServerDate(data.date[0]),
          to: DateUtility.dateToServerDate(data.date[1]),
          marketplace_region_id: marketplaceRegionId,
          [isDeal ? 'units' : 'percentage']:
            data.percentage + (isDeal ? 0 : 100),
        };

        if (eventId) {
          payload[key] = payload[key].map(d => {
            if (d._id === eventId) {
              return { ...d, _id: eventId, ...item };
            }
            return d;
          });
        } else {
          payload[key].push(item);
        }
      }

      await SCMService.updateItemSeasonSpike(itemId, payload);
      await refreshTimeline();
      await forceRefreshSales(true);

      if (isDelete) {
        setEventId('');
      }

      setAddEventCount(0);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
      setDeleting(false);
    }
  };

  const handleDelete = async () => await submit(null, true);

  useEffect(() => {
    setFormData({
      country: formData.country,
      from: formData.date[0],
      name: formData.name,
      to: formData.date[1],
      type: isLightningDeals ? 'units' : 'percentage',
      units: formData.percentage,
    });
  }, [
    formData.country,
    formData.date,
    formData.name,
    formData.percentage,
    isLightningDeals,
  ]);

  const eventLabel = EVENT_TYPE_OPTIONS.find(
    d => d.value === formDefaultValues?.type,
  )?.text;

  return (
    <form onSubmit={handleSubmit(values => submit(values))} autoComplete="off">
      <div className="sk:flex sk:flex-wrap sk:-mx-4">
        <div className="sk:w-6/12 sk:flex-none sk:px-4">
          <DropdownFormField
            control={control}
            error={errors?.type}
            label="Event Type"
            name="type"
            options={EVENT_TYPE_OPTIONS}
            placeholder="Event Type"
            disabled={eventId}
          />
        </div>
        <div className="sk:w-6/12 sk:flex-none sk:px-4">
          {/* @ts-expect-error - TS2739: Type '{ control: Control<{ country: any; type: string; date:... */}
          <RangeDateFormField
            control={control}
            error={errors?.date}
            fieldClass=""
            hint={errors?.date}
            label="Date Range"
            minDate={new Date()}
            name="date"
            selectsRange
            resetUTC={false}
          />
        </div>
        <div className="sk:w-6/12 sk:flex-none sk:px-4 sk:mb-5!">
          <ControlledTextFormField
            autoFocus
            control={control}
            error={errors?.name}
            fieldClass="sk:mb-0!"
            // @ts-expect-error - TS2322: Type 'string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined'...
            hint={errors?.name?.message}
            label="Event Name"
            name="name"
            placeholder="Event Name"
          />
        </div>
        <div className="sk:w-6/12 sk:flex-none sk:px-4 sk:mb-5!">
          <MaskedNumberFormField
            control={control}
            error={errors?.percentage}
            fieldClass="sk:mb-0!"
            // @ts-expect-error - TS18048: 'errors.percentage.message' is possibly 'undefined'.
            hint={errors?.percentage?.message.replace('Percentage', unitLabel)}
            label={unitLabel}
            name="percentage"
            placeholder={unitLabel}
            maskOptions={{
              allowLeadingZeroes: false,
              ...(!isLightningDeals
                ? { allowDecimal: true, decimalSymbol: '.' }
                : {}),
            }}
            inputRight={
              !isLightningDeals ? <SimpleInputRight>%</SimpleInputRight> : <></>
            }
          />
        </div>
        <div className="sk:w-full sk:flex-none sk:px-4">
          {/* @ts-expect-error - TS2740: Type '{ className: string; control: Control<{ country: any; ... */}
          <CountryDropdownFormField
            className="country-select"
            control={control}
            disabled
            // @ts-expect-error - TS2339: Property 'marketplace_region_id' does not exist on type 'FieldErrors<{ country: ...
            error={errors.marketplace_region_id}
            // @ts-expect-error - TS2339: Property 'marketplace_region_id' does not exist on type 'FieldErrors<{ country: ...
            hint={errors.marketplace_region_id?.message}
            label="Country"
            name="country"
            options={marketplaceDropdown}
          />
        </div>
        <div className="sk:w-full sk:flex-none sk:px-4">
          <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
            <HoverBorderButton
              type="button"
              onClick={() => {
                reset({ ...formDefaultValues });
                setAddEventCount(0);
                setEventId('');
              }}
              disabled={loading}
            >
              Cancel
            </HoverBorderButton>
            {eventId && (
              <DeleteConfirmWithButton
                buttonClass="sk:ml-0!"
                buttonText="Delete"
                // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'loading' does not exist i...
                deleteButtonProps={{ disabled: !!loading, loading: deleting }}
                disabled={!!loading}
                onConfirm={handleDelete}
                title={`Delete ${eventLabel} Event "${formDefaultValues.name}"`}
                size="tiny"
              />
            )}
            <StyledButton
              className="sk:ml-0!"
              disabled={loading}
              loading={loading && !deleting}
              type="submit"
            >
              {eventId ? 'Update' : 'Create'}
            </StyledButton>
          </div>
        </div>
      </div>
    </form>
  );
}

export default SeasonalityEventForm;
